import { cva, type VariantProps } from 'class-variance-authority';
import { LoaderCircleIcon, type LucideIcon } from 'lucide-react';
import {
  createElement,
  isValidElement,
  type HTMLAttributes,
  type PropsWithChildren,
  type ReactElement,
} from 'react';

import { Skeleton } from '@/components/core/skeleton';
import { cn } from '@/shared/helpers';

const timelineMarkerVariants = cva(
  'z-10 flex size-6 items-center justify-center rounded-full ring-4 ring-background [&>svg]:size-3.5',
  {
    variants: {
      type: {
        success: 'bg-state-success text-state-success-foreground',
        progress: 'bg-primary text-primary-foreground',
        observe: 'bg-state-observe text-state-observe-foreground',
        alert: 'bg-state-alert text-state-alert-foreground',
        inactive: 'bg-state-inactive text-state-inactive-foreground',
      },
    },
    defaultVariants: {
      type: 'inactive',
    },
  },
);

export type TimelineType = NonNullable<VariantProps<typeof timelineMarkerVariants>['type']>;

export function Timeline({ className, ...props }: HTMLAttributes<HTMLOListElement>) {
  return (
    <ol
      className={cn(
        'relative m-0 grid list-none gap-0 p-0 text-sm before:absolute before:top-1 before:bottom-1 before:left-3 before:w-0.5 before:-translate-x-1/2 before:bg-border',
        className,
      )}
      {...props}
    />
  );
}

export function TimelineSectionMarker({
  className,
  children,
  ...props
}: PropsWithChildren<HTMLAttributes<HTMLLIElement>>) {
  return (
    <li
      className={cn('relative grid grid-cols-[1.5rem_minmax(0,1fr)] gap-x-3 pb-3', className)}
      {...props}
    >
      <span className="z-10 flex size-6 items-center justify-center bg-background" aria-hidden>
        <span className="size-2 rounded-full bg-border" />
      </span>
      <span className="min-w-0 pt-0.5 font-heading text-xs leading-5 font-semibold tracking-wide text-muted-foreground uppercase">
        {children}
      </span>
    </li>
  );
}

export type TimelineEntryProps = PropsWithChildren<
  Omit<HTMLAttributes<HTMLLIElement>, 'type'> & {
    icon: LucideIcon | ReactElement;
    type?: TimelineType;
  }
>;

export function TimelineEntry({
  icon: Icon,
  type,
  className,
  children,
  ...props
}: TimelineEntryProps) {
  const icon = isValidElement(Icon) ? Icon : createElement(Icon as LucideIcon);

  return (
    <li
      className={cn(
        'relative grid min-w-0 grid-cols-[1.5rem_minmax(0,1fr)] gap-x-3 pb-4 last:pb-0',
        className,
      )}
      {...props}
    >
      <span className={timelineMarkerVariants({ type })} aria-hidden>
        {icon}
      </span>
      <div className="min-w-0 pt-0.5">{children}</div>
    </li>
  );
}

export function TimelineSkeletonEntry({ className, ...props }: HTMLAttributes<HTMLLIElement>) {
  return (
    <TimelineEntry
      icon={<LoaderCircleIcon className="animate-spin" />}
      type="progress"
      className={className}
      {...props}
    >
      <TimelineEntryHeader>
        <TimelineEntryTitle>
          <Skeleton className="h-4 w-32" aria-hidden />
        </TimelineEntryTitle>
        <TimelineEntryMeta>
          <Skeleton className="h-3 w-20" aria-hidden />
        </TimelineEntryMeta>
      </TimelineEntryHeader>
      <TimelineEntryBody>
        <Skeleton className="h-3 w-full" aria-hidden />
      </TimelineEntryBody>
    </TimelineEntry>
  );
}

export function TimelineEntryHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1', className)}
      {...props}
    />
  );
}

export function TimelineEntryTitle({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('min-w-0 font-medium wrap-break-word text-foreground', className)}
      {...props}
    />
  );
}

export function TimelineEntryMeta({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('inline-flex min-w-0 items-center text-xs text-muted-foreground', className)}
      {...props}
    />
  );
}

export function TimelineEntryBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('mt-1 min-w-0 wrap-break-word text-muted-foreground', className)}
      {...props}
    />
  );
}
